import { useEffect, useState } from "react";
import { Sparkles, X } from "lucide-react";
import { T } from "../lib/i18n";

// Briefly play a chime via Web Audio API (no external file needed).
function playChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const notes = [880, 1108.73, 1318.51]; // A5, C#6, E6 (major chord)
    const start = ctx.currentTime;
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, start + i * 0.12);
      gain.gain.exponentialRampToValueAtTime(0.18, start + i * 0.12 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + i * 0.12 + 0.9);
      osc.connect(gain).connect(ctx.destination);
      osc.start(start + i * 0.12);
      osc.stop(start + i * 0.12 + 0.95);
    });
    setTimeout(() => ctx.close().catch(() => {}), 1500);
  } catch {}
}

function vibrate() {
  try {
    if (navigator.vibrate) navigator.vibrate([200, 80, 200, 80, 400]);
  } catch {}
}

export default function CocktailOverlay({ cocktail, onClose }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!cocktail) return;
    setVisible(true);
    playChime();
    vibrate();
    const t = setTimeout(() => {
      setVisible(false);
      setTimeout(onClose, 400);
    }, 12000);
    return () => clearTimeout(t);
  }, [cocktail, onClose]);

  if (!cocktail) return null;

  return (
    <div
      className={`fixed inset-0 z-[200] flex items-center justify-center p-6 transition-opacity duration-500 ${
        visible ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
      style={{
        background:
          "radial-gradient(circle at 50% 30%, color-mix(in srgb, var(--motif-primary) 50%, black) 0%, rgba(0,0,0,0.92) 65%)",
      }}
      onClick={() => { setVisible(false); setTimeout(onClose, 400); }}
      data-testid="cocktail-overlay"
    >
      <button
        onClick={(e) => { e.stopPropagation(); setVisible(false); setTimeout(onClose, 400); }}
        className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/15 backdrop-blur flex items-center justify-center text-white"
        data-testid="cocktail-overlay-close"
      >
        <X className="w-5 h-5" />
      </button>

      <div className="text-center text-white max-w-xl fadein">
        <Sparkles
          className="w-10 h-10 mx-auto mb-4 text-white/90"
          strokeWidth={1.2}
          style={{ animation: "shimmer 3s ease-in-out infinite" }}
        />
        <div className="text-[11px] tracking-[0.3em] uppercase text-white/70 mb-3">{T("now pouring")}</div>
        <h2
          className="font-display text-6xl sm:text-7xl leading-tight mb-5"
          style={{
            color: "var(--motif-primary)",
            textShadow: "0 4px 24px rgba(0,0,0,0.5)",
          }}
        >
          {cocktail.name}
        </h2>
        {cocktail.description && (
          <p className="font-display text-xl text-white/85 italic mb-6">{cocktail.description}</p>
        )}
        {cocktail.ingredients?.length > 0 && (
          <div className="flex flex-wrap justify-center gap-2 mb-2">
            {cocktail.ingredients.map((ing, i) => (
              <span
                key={`${ing}-${i}`}
                className="px-3 py-1.5 rounded-full text-xs tracking-wide"
                style={{
                  background: "rgba(255,255,255,0.12)",
                  border: "1px solid rgba(255,255,255,0.2)",
                  color: "white",
                }}
              >
                {ing}
              </span>
            ))}
          </div>
        )}
        {cocktail.garnish && (
          <div className="text-xs tracking-[0.22em] uppercase text-white/60 mt-4">
            with {cocktail.garnish}
          </div>
        )}
        <div className="mt-8 text-[10px] tracking-[0.3em] uppercase text-white/50">
          tap anywhere to dismiss
        </div>
      </div>
    </div>
  );
}
